"use client";

import { useTranslations } from "@/i18n/client";
import type { ProductSizes } from "@/lib/centra/formatters/sizes";
import { clsx } from "@frend-digital/ui";
import { useQueryParams } from "@frend-digital/ui/next";
import { Suspense } from "react";
import styles from "./index.module.css";

// eslint-disable-next-line no-unused-vars
type SizeAction = (sizeId: string, name: string) => void;

const SizeList = ({
	sizes,
	selectedSize,
	action,
}: {
	sizes: ProductSizes;
	selectedSize: string | null;
	action?: SizeAction;
}) => {
	const isOneSize = sizes.length === 1;
	return (
		<div className={styles.sizes}>
			{sizes.map((size) => (
				<SizeButton
					action={action}
					key={size.item}
					label={size.name}
					sizeId={size.item}
					soldOut={!size.stock}
					selected={selectedSize === size.name}
					isOneSize={isOneSize}
				/>
			))}
		</div>
	);
};

export const UnsuspendedSizes = ({ sizes }: { sizes: ProductSizes }) => {
	const searchParams = useQueryParams({
		replace: true,
	});

	const handleSize: SizeAction = (_, name) => {
		searchParams.set("size", name);
	};

	const size = searchParams.get("size");

	return <SizeList action={handleSize} sizes={sizes} selectedSize={size} />;
};

export const SizeSelector = ({ sizes }: { sizes: ProductSizes }) => {
	return (
		<Suspense fallback={<SizeList sizes={sizes} selectedSize={null} />}>
			<UnsuspendedSizes sizes={sizes} />
		</Suspense>
	);
};

const SizeButton = ({
	label,
	sizeId,
	soldOut,
	selected,
	action,
	isOneSize,
}: {
	label?: string;
	sizeId?: string;
	soldOut?: boolean;
	selected?: boolean;
	// eslint-disable-next-line no-unused-vars
	action?: SizeAction;
	isOneSize?: boolean;
}) => {
	const t = useTranslations("pdp");
	return (
		<button
			onClick={() => action?.(sizeId!, label!)}
			className={clsx(
				styles.sizeButton,
				soldOut && styles.soldOut,
				selected && styles.selected,
				isOneSize && styles.oneSize,
			)}>
			<span className={clsx(styles.label, styles.oneSize)}>
				{isOneSize ? t("oneSize") : label}
			</span>
		</button>
	);
};
